import PublicShell from "../components/PublicShell";
import { Mail, Phone, MessageCircle, MapPin, Building2, Globe, ShieldCheck } from "lucide-react";

/**
 * Sprint 4 — Public Contact / Grievance page.
 * Renders Factskoot (India) and Destineze (UAE) contact blocks plus the
 * Smart Support / ticket links.
 */
export default function ContactPage() {
  return (
    <PublicShell>
      <section className="relative overflow-hidden bg-brand-navy-dark text-brand-champagne">
        <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
        <div aria-hidden className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />
        <div aria-hidden className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
        <div className="relative max-w-5xl mx-auto px-6 py-20">
          <p className="eyebrow text-brand-gold flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> Diplomexa Contact &amp; Grievance
          </p>
          <h1 className="mt-4 headline-luxury text-4xl sm:text-5xl text-brand-champagne">
            We&apos;re here to{" "}
            <span className="italic font-display font-medium text-brand-gold">help</span>.
          </h1>
          <div className="divider-gold-hairline mt-7 max-w-md" />
          <p className="mt-7 text-base text-brand-champagne/70 max-w-2xl leading-relaxed">
            Diplomexa is operated by two entities. Use the contact details that match
            your billing geography. For instant help, open{" "}
            <span className="text-brand-gold font-semibold">Diplomexa Smart Support</span>{" "}
            from your dashboard.
          </p>
        </div>
      </section>

      <section className="bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-6" data-testid="contact-cards">
          <Card icon={Building2} title="India Billing — Factskoot">
            <p><strong>Factskoot Eduwellness Private Limited</strong></p>
            <p className="text-xs text-slate-600 mt-1">
              No. 43, WeWork Galaxy, Shanthala Nagar,<br />
              Residency Road, Bengaluru 560025, India
            </p>
            <div className="mt-3 space-y-1.5 text-xs">
              <Row icon={Mail} text={<a href="mailto:info@factskoot.com" className="text-[#0F4C81] underline">info@factskoot.com</a>} />
              <Row icon={Phone} text="+91-80-62177852" />
              <Row icon={MessageCircle} text="WhatsApp: +91 9844139333" />
              <Row icon={Globe} text={<a href="https://www.factskoot.com" target="_blank" rel="noreferrer" className="text-[#0F4C81] underline">www.factskoot.com</a>} />
            </div>
          </Card>

          <Card icon={Building2} title="International Billing — Destineze">
            <p><strong>Destineze Educational Voyages FZCO</strong></p>
            <p className="text-xs text-slate-600 mt-1">
              IFZA Business Park, Dubai Silicon Oasis,<br />
              Dubai, United Arab Emirates
            </p>
            <div className="mt-3 space-y-1.5 text-xs">
              <Row icon={Mail} text={<a href="mailto:info@destineze.com" className="text-[#0F4C81] underline" data-testid="destineze-email">info@destineze.com</a>} />
              <Row icon={Phone} text={<span data-testid="destineze-phone">+971 50 488 3656</span>} />
              <Row icon={MessageCircle} text="WhatsApp: +971 50 488 3656" />
              <Row icon={Globe} text={<a href="https://www.destineze.com" target="_blank" rel="noreferrer" className="text-[#0F4C81] underline">www.destineze.com</a>} />
            </div>
          </Card>

          <Card icon={ShieldCheck} title="Refund & Cancellation — by entity">
            <p className="text-xs text-slate-600">
              Refunds always follow the original billing entity and payment gateway.
            </p>
            <ul className="mt-2 space-y-1 text-[11px] text-slate-600 list-disc pl-5">
              <li><strong>INR / Razorpay payments:</strong> handled by Factskoot only — write to{" "}
                <a className="text-[#0F4C81] underline" href="mailto:info@factskoot.com">info@factskoot.com</a>.
              </li>
              <li><strong>Foreign-currency / Stripe payments:</strong> handled by Destineze only — write to{" "}
                <a className="text-[#0F4C81] underline" href="mailto:info@destineze.com">info@destineze.com</a>.
              </li>
            </ul>
            <p className="mt-2 text-xs text-slate-600">
              You can also raise a request directly from your{" "}
              <a className="text-[#0F4C81] underline" href="/dashboard/billing">Billing Center</a>.
            </p>
          </Card>

          <Card icon={ShieldCheck} title="Privacy / Data Request — by entity">
            <p className="text-xs text-slate-600">
              Use the <a className="text-[#0F4C81] underline" href="/dashboard/privacy">Privacy &amp; Data Center</a>{" "}
              to download or delete your data, or email the entity that bills you:
            </p>
            <ul className="mt-2 space-y-1 text-[11px] text-slate-600 list-disc pl-5">
              <li>India / Factskoot —{" "}
                <a className="text-[#0F4C81] underline" href="mailto:info@factskoot.com">info@factskoot.com</a>{" "}with subject "Privacy request".
              </li>
              <li>International / Destineze —{" "}
                <a className="text-[#0F4C81] underline" href="mailto:info@destineze.com">info@destineze.com</a>{" "}with subject "Privacy request".
              </li>
            </ul>
            <p className="mt-2 text-[11px] text-slate-500">
              India: Digital Personal Data Protection Act, 2023.<br />
              UAE: UAE Personal Data Protection Law (Federal Decree-Law No. 45 of 2021).
            </p>
          </Card>

          <Card icon={ShieldCheck} title="Grievance / Escalation — by entity">
            <p className="text-xs text-slate-600">
              For grievances or formal complaints, write to the entity that bills you with
              the subject line <strong>"Grievance"</strong> and reference your registration or invoice
              number. We respond within 7 working days.
            </p>
            <ul className="mt-2 space-y-1 text-[11px] text-slate-600 list-disc pl-5">
              <li>India billing —{" "}
                <a className="text-[#0F4C81] underline" href="mailto:info@factskoot.com">info@factskoot.com</a>{" "}· +91-80-62177852.
              </li>
              <li>International billing —{" "}
                <a className="text-[#0F4C81] underline" href="mailto:info@destineze.com">info@destineze.com</a>{" "}· +971 50 488 3656.
              </li>
            </ul>
            <p className="mt-2 text-[11px] text-slate-500">
              The Grievance Officer and DPO for each entity will be published here after
              the final legal review.
            </p>
          </Card>

          <Card icon={MessageCircle} title="Smart Support">
            <p className="text-xs text-slate-600">
              Log in to your dashboard and click the gold concierge button bottom-right
              to chat with <strong>Diplomexa Smart Support</strong> — your conference
              concierge that knows your event, allocation, learning progress and billing.
            </p>
          </Card>
        </div>
      </section>
    </PublicShell>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#0F4C81]" />
        <h3 className="text-base font-semibold text-[#0A192F]">{title}</h3>
      </div>
      <div className="mt-3 text-sm text-slate-700">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, text }) {
  return (
    <p className="flex items-center gap-2 text-slate-600">
      <Icon className="h-3.5 w-3.5 text-slate-400" /> {text}
    </p>
  );
}
